import { Link, useOutletContext } from 'react-router';
import {
  Badge,
  EmptyState,
  InlineAlert,
  Panel,
  cn,
  PRESS,
  formatDate,
  formatMoney,
  formatPercentage,
  type BadgeTone,
} from 'escrowbuild-ui';

import { useT, type AppTranslationKey } from '@/i18n';
import {
  PARTICIPATIONS,
  findInvestor,
  participationsOf,
  projectTotals,
  type Participation,
  type Project,
} from '@/data/domain';

/**
 * `ProjectShell` resolves the development once and hands it down, so this tab
 * cannot derive a name, a phase or a balance differently from the five beside it.
 */
interface ProjectOutlet {
  project: Project;
  totals: ReturnType<typeof projectTotals>;
}

const PARTICIPATION_TONE: Record<Participation['status'], BadgeTone> = {
  confirmed: 'success',
  pending: 'warning',
  draft: 'neutral',
};

/* The row badge used to print the wire code, and `confirmed` is the API's word
   rather than the reader's. These three are the shared status vocabulary's. */
const PARTICIPATION_LABEL: Record<Participation['status'], AppTranslationKey> = {
  confirmed: 'status.participation.confirmed',
  pending: 'status.participation.pending',
  draft: 'status.participation.draft',
};

const TH = 'bg-surface-alt px-3 py-2 font-display text-2xs font-semibold uppercase tracking-label text-muted';
const TD = 'px-3 py-2 text-body';

const LINK = cn(
  'font-medium text-heading underline decoration-line-strong underline-offset-2',
  'transition-colors duration-[var(--dur-fast)] hover:decoration-brand',
  PRESS,
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
);

/**
 * The cap table of one development.
 *
 * `participationsOf` drops every zero-share record, which is why the totals here
 * are allowed to be trusted: the rows that remain are the ones that carry a
 * share. Prospects are listed apart, so filtering them out of the table does not
 * hide them from the reader.
 */
/* The cell's link is a control of its own at roughly 13px of text, so it takes
   the 44px overlay; the 48px rows leave the overlays no room to collide. */
const CELL_LINK = cn(LINK, 'relative dhd-target');

export function ProjectInvestorsPage() {
  const t = useT();
  const { project } = useOutletContext<ProjectOutlet>();

  const participants = participationsOf(project.id);
  const prospects = PARTICIPATIONS.filter(
    (record) => record.projectId === project.id && record.sharePercent === 0,
  );

  const contributed = participants.reduce((sum, record) => sum + record.contributed, 0);
  const committed = participants.reduce((sum, record) => sum + record.committed, 0);
  const outstanding = committed - contributed;
  const shareSum = participants.reduce((sum, record) => sum + record.sharePercent, 0);
  const callsIssued = participants.reduce((sum, record) => sum + record.callsIssued, 0);
  const callsSettled = participants.reduce((sum, record) => sum + record.callsSettled, 0);
  const documents = participants.reduce((sum, record) => sum + record.documents, 0);

  const reconciles = contributed === project.contributed;
  const shareCloses = shareSum + project.ownershipShare === 100;

  return (
    <div className="flex flex-col gap-6">
      <InlineAlert
        tone={reconciles && shareCloses ? 'success' : 'error'}
        title={
          reconciles && shareCloses
            ? t('project.investors.reconciles')
            : t('project.investors.mismatch')
        }
        description={
          reconciles && shareCloses
            ? t('project.investors.reconcilesBody', {
                contributed: formatMoney(contributed),
                committed: formatMoney(project.contributed),
                investorShare: formatPercentage(shareSum, { precision: 0 }),
                builderShare: formatPercentage(project.ownershipShare, { precision: 0 }),
              })
            : t('project.investors.mismatchBody', {
                contributed: formatMoney(contributed),
                difference: formatMoney(Math.abs(contributed - project.contributed)),
                shareSum: formatPercentage(shareSum, { precision: 0 }),
                expected: formatPercentage(100 - project.ownershipShare, { precision: 0 }),
              })
        }
      />

      <section className="rounded-xs border border-line bg-surface">
        <header className="flex flex-wrap items-start justify-between gap-4 border-b border-line-subtle px-4 py-3">
          <div className="min-w-0">
            <h2 className="font-display text-lg font-semibold uppercase tracking-title text-heading">
              {t('project.investors.capTable')}
            </h2>
            <p className="mt-1 text-sm text-muted">
              {t('project.investors.capTableHint', { name: project.name })}
            </p>
          </div>
        </header>

        {participants.length === 0 ? (
          <div className="p-4">
            <EmptyState
              variant="cards"
              title={t('project.investors.emptyTitle')}
              description={t('project.investors.emptyBody', { name: project.name })}
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <caption className="dhd-sr-only">
                {t('project.investors.tableCaption', { name: project.name })}
              </caption>
              <thead>
                <tr className="h-12 border-b-2 border-line-strong">
                  <th scope="col" className={cn(TH, 'text-left')}>{t('project.investors.col.participant')}</th>
                  <th scope="col" className={cn(TH, 'text-left')}>{t('project.investors.col.joined')}</th>
                  <th scope="col" className={cn(TH, 'text-right')}>{t('project.investors.col.share')}</th>
                  <th scope="col" className={cn(TH, 'text-right')}>{t('project.investors.col.committed')}</th>
                  <th scope="col" className={cn(TH, 'text-right')}>{t('project.shell.contributed')}</th>
                  <th scope="col" className={cn(TH, 'text-right')}>{t('project.investors.col.outstanding')}</th>
                  <th scope="col" className={cn(TH, 'text-right')}>{t('project.investors.col.callsSettled')}</th>
                  <th scope="col" className={cn(TH, 'text-right')}>{t('project.investors.col.documents')}</th>
                  <th scope="col" className={cn(TH, 'text-left')}>{t('common.status')}</th>
                </tr>
              </thead>
              <tbody>
                {participants.map((record) => {
                  const investor = findInvestor(record.investorId);
                  return (
                    <tr key={record.id} className="h-12 border-b border-line-subtle hover:bg-surface-alt">
                      <th scope="row" className={cn(TD, 'text-left font-normal')}>
                        <Link to={`/investors/${record.investorId}`} className={CELL_LINK}>
                          {investor?.name ?? record.investorId}
                        </Link>
                        <span className="mt-0.5 block text-2xs text-muted">
                          {investor ? investor.city : null}
                        </span>
                      </th>
                      <td className={TD}>{formatDate(record.joinedAt)}</td>
                      <td className={cn(TD, 'text-right font-mono')}>
                        {formatPercentage(record.sharePercent, { precision: 0 })}
                      </td>
                      <td className={cn(TD, 'text-right font-mono')}>{formatMoney(record.committed)}</td>
                      <td className={cn(TD, 'text-right font-mono')}>{formatMoney(record.contributed)}</td>
                      <td className={cn(TD, 'text-right font-mono')}>
                        {formatMoney(record.committed - record.contributed)}
                      </td>
                      <td className={cn(TD, 'text-right font-mono')}>
                        {record.callsSettled} / {record.callsIssued}
                      </td>
                      <td className={cn(TD, 'text-right')}>{record.documents}</td>
                      <td className={TD}>
                        <Badge tone={PARTICIPATION_TONE[record.status]} variant="subtle" size="sm">
                          {t(PARTICIPATION_LABEL[record.status])}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-line-strong bg-surface-alt">
                  <th
                    scope="row"
                    colSpan={2}
                    className="px-3 py-2 text-left font-display text-2xs font-semibold uppercase tracking-label text-heading"
                  >
                    <span className="dhd-sr-only">{t('common.total')} </span>
                    {t('project.investors.count', { count: participants.length })}
                  </th>
                  <td className="px-3 py-2 text-right font-mono text-sm text-heading">
                    {formatPercentage(shareSum, { precision: 0 })}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-sm text-heading">
                    {formatMoney(committed)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-sm text-heading">
                    {formatMoney(contributed)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-sm text-heading">
                    {formatMoney(outstanding)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-sm text-heading">
                    {callsSettled} / {callsIssued}
                  </td>
                  <td className="px-3 py-2 text-right text-sm text-heading">{documents}</td>
                  <td className="px-3 py-2" />
                </tr>
                <tr className="border-t border-line-subtle bg-surface-alt">
                  <th
                    scope="row"
                    colSpan={2}
                    className="px-3 py-2 text-left font-display text-2xs font-semibold uppercase tracking-label text-muted"
                  >
                    {t('project.investors.retained')}
                  </th>
                  <td className="px-3 py-2 text-right font-mono text-sm text-muted">
                    {formatPercentage(project.ownershipShare, { precision: 0 })}
                  </td>
                  <td colSpan={6} className="px-3 py-2 text-2xs text-muted">
                    {t('project.investors.retainedNote', { name: project.name })}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </section>

      {prospects.length > 0 ? (
        <Panel
          title={t('project.investors.pipelineTitle')}
          description={t('project.investors.pipelineHint')}
        >
          <ul className="flex list-none flex-col gap-4 p-0">
            {prospects.map((record) => {
              const investor = findInvestor(record.investorId);
              return (
                <li key={record.id} className="flex flex-wrap items-start justify-between gap-3">
                  <span className="min-w-0">
                    <Link to={`/investors/${record.investorId}`} className={CELL_LINK}>
                      {investor?.name ?? record.investorId}
                    </Link>
                    <span className="mt-0.5 block text-2xs text-muted">
                      {t('project.investors.entered', { date: formatDate(record.joinedAt) })} ·{' '}
                      {t('project.investors.documentsShared', { count: record.documents })} ·{' '}
                      {investor ? investor.email : null}
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    <Badge tone="neutral" variant="subtle" size="sm">
                      {t('project.investors.shareBadge', {
                        share: formatPercentage(record.sharePercent, { precision: 0 }),
                      })}
                    </Badge>
                    <Badge tone={PARTICIPATION_TONE[record.status]} variant="subtle" size="sm">
                      {t(PARTICIPATION_LABEL[record.status])}
                    </Badge>
                  </span>
                </li>
              );
            })}
          </ul>
          <p className="mt-4 text-2xs text-muted">{t('project.investors.pipelineNote')}</p>
        </Panel>
      ) : null}
    </div>
  );
}
